import "./category.css";

import React from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { useParams, useNavigate, useLocation, matchRoutes } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

import { homeSelectors, notifSelectors } from "../../logic/rootSelectors";
import _home from "../../logic/home";
import { CATEGORY_TYPE, LOADING_STATE, LS_ITEM, PAGE_ROUTES } from "../../utils/stateTypes";
import MainContent from "../general/MainContent";
import Breadcrumbs from "../sections/Breadcrumbs";
import HeadBanner from "../sections/HeadBanner";
import InfoSection from "../sections/InfoSection";
import ProductBlock from "../product/ProductBlock";
import CustomCategory from "./custom/CustomCategory";

const PAGE_SIZE = 24;

const styles = {
  pageButton: {
    width: 130,
  },
};


const Category = ({
  categoryDetails, products, page, getLoadState,
  getCategoryDetails, getCategoryProducts,
}) => {
  let { categoryUrl } = useParams();
  const navigate = useNavigate();

  //Check for the custom rolex route and update the categoryUrl accordingly
  const location = useLocation();
  let match = matchRoutes(Object.values(PAGE_ROUTES).map((route) => ({ path: route })), location.pathname);
  let route = {};
  if (match && (match.length > 0)) {
    route = match[0].route;
  }
  if (route.path === PAGE_ROUTES.ROLEX) {
    categoryUrl = "rolex";
  }

  const categoryDetailsLS = getLoadState(`${LS_ITEM.CATEGORY_DETAILS_LOAD}${categoryUrl}`);
  const categoryProductsLS = getLoadState(`${LS_ITEM.CATEGORY_PRODUCTS_LOAD}${categoryUrl}`);


  const loadCatCB = React.useCallback(() => {
    const navigateTo404 = () => navigate(PAGE_ROUTES.NOT_FOUND);
    getCategoryDetails(categoryUrl, navigateTo404);
    getCategoryProducts(categoryUrl, 0, PAGE_SIZE);
  }, [categoryUrl, getCategoryDetails]);

  React.useEffect(() => {
    //Skip load if already loaded and correct category
    if ((categoryDetailsLS === LOADING_STATE.LOADED) && (categoryDetails.url === categoryUrl)) {
      return;
    }
    loadCatCB();
  }, [categoryUrl]);

  return (
    <MainContent loadItems={[{ loadState: categoryDetailsLS, loadFn: () => {} }]}>
      <Helmet>
        <title>Charles Greig | {categoryDetails.name}</title>
      </Helmet>

      {
        ((categoryDetailsLS === LOADING_STATE.LOADED) && (categoryDetails.customPageName === null)) && (
          <React.Fragment>
            <HeadBanner
              imageLg={categoryDetails.headerImage}
              headPreGreen={categoryDetails.headerTextPre}
              headWhite={categoryDetails.headerText}
              headPostGreen={categoryDetails.headerTextPost}
            />
            <span id={"category"} />

            <Breadcrumbs
              crumbs={[
                { text: "Home", link: "/" },
                { text: (categoryDetails.type === CATEGORY_TYPE.TIMEPIECES) ? "Timepieces" : "Jewellery", link: (categoryDetails.type === CATEGORY_TYPE.TIMEPIECES) ? PAGE_ROUTES.TIMEPIECES : PAGE_ROUTES.JEWELLERY },
                { text: categoryDetails.name, link: "" },
              ]}
            />

            {
              (categoryProductsLS === LOADING_STATE.LOADED) && (
                <React.Fragment>
                  <section className={"categories-section"}>
                    {
                      products.map((product) => (
                        <ProductBlock
                          key={product.id}
                          product={product}
                        />
                      ))
                    }
                    {
                      (products.length === 0) && (
                        <p className={"category-no-results"}>Products coming soon</p>
                      )
                    }
                    <div className={"category-pagination-section"}>
                      <Button
                        variant={"contained"}
                        color={"primary"}
                        disabled={(page <= 0)}
                        startIcon={<ChevronLeft />}
                        style={styles.pageButton}
                        onClick={() => {
                          document.getElementById("category").scrollIntoView({ behavior: "smooth" });
                          getCategoryProducts(categoryUrl, page - 1, PAGE_SIZE);
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        variant={"contained"}
                        color={"primary"}
                        disabled={(products.length < PAGE_SIZE)}
                        endIcon={<ChevronRight />}
                        style={styles.pageButton}
                        onClick={() => {
                          document.getElementById("category").scrollIntoView({ behavior: "smooth" });
                          getCategoryProducts(categoryUrl, page + 1, PAGE_SIZE);
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </section>
                </React.Fragment>
              )
            }
            
            {
              (categoryProductsLS === LOADING_STATE.LOADING) && (
                <div className={"loading-cont"}>
                  <CircularProgress
                    size={80}
                    color={"secondary"}
                  />
                  <p>Loading products</p>
                </div>
              )
            }

            {
              categoryDetails.infoSections.map((infoSection, index) => (
                <InfoSection
                  key={infoSection.id}
                  info={{
                    image: infoSection.image,
                    headPreGreen: infoSection.headPre,
                    headWhite: infoSection.head,
                    headPostGreen: infoSection.headPost,
                    subHeadHtml: infoSection.body,
                    linkUrl: infoSection.linkUrl,
                    linkLabel: infoSection.linkLabel,
                  }}
                  isVariant={(index % 2 === 0)}
                />
              ))
            }
          </React.Fragment>
        )
      }

      {
        ((categoryDetailsLS === LOADING_STATE.LOADED) && (categoryDetails.customPageName !== null)) && (
          <CustomCategory pageName={categoryDetails.customPageName} />
        )
      }
    </MainContent>
  );
};

const mapStateToProps = (state) => {
  return {
    categoryDetails: homeSelectors.getCategoryDetails(state),
    products: homeSelectors.getCategoryProducts(state),
    page: homeSelectors.getPage(state),
    getLoadState: notifSelectors.getLoadState(state),
  };
};

const connected = connect(
  mapStateToProps,
  {
    getCategoryDetails: _home.actions.getCategoryDetails,
    getCategoryProducts: _home.actions.getCategoryProducts,
  },
)(Category);

export default connected;